import type { BaseOptionType } from "antd/lib/select"

import { FINANCE_ITEM_STATUSES } from "../../../../constants/finance-item-constants.ts"

type PartialFinance = { status?: number }

// A COMPLETED partial is the signal the backend uses to derive PARTIAL, so the
// Pending/Partial gates key on it — exactly one of the two fits a line at once.
export const hasCompletedPartialPayment = (
  partialFinances: PartialFinance[] | undefined
): boolean =>
  (partialFinances ?? []).some(
    (payment) => payment.status === FINANCE_ITEM_STATUSES.COMPLETED
  )

// The backend rejects a revert to Planned while a line has ANY non-deleted
// partial, regardless of that partial's own status — so the Planned gate keys
// on this, not on hasCompletedPartialPayment, to stay in sync with the 400.
export const hasAnyPartialPayment = (
  partialFinances: PartialFinance[] | undefined
): boolean => (partialFinances ?? []).length > 0

export const buildStatusOptions = (
  baseOptions: BaseOptionType[] | undefined,
  partialFinances: PartialFinance[] | undefined
): BaseOptionType[] => {
  const hasCompletedPayment = hasCompletedPartialPayment(partialFinances)
  const hasAnyPayment = hasAnyPartialPayment(partialFinances)

  return (baseOptions ?? []).map((option) => {
    if (option.value === FINANCE_ITEM_STATUSES.PENDING) {
      return { ...option, disabled: hasCompletedPayment }
    }
    if (option.value === FINANCE_ITEM_STATUSES.PARTIAL) {
      return { ...option, disabled: !hasCompletedPayment }
    }
    if (option.value === FINANCE_ITEM_STATUSES.PLANNED) {
      return { ...option, disabled: hasAnyPayment }
    }
    // New is auto-assigned to a fresh line; it leaves New on a payment or an
    // explicit change and can never be selected manually.
    if (option.value === FINANCE_ITEM_STATUSES.NEW) {
      return { ...option, disabled: true }
    }
    return option
  })
}
